import { FC, useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useParcels } from "@/context/Parcels.context";
import { useTheme } from "@/context/Theme.context";

import { Button, InnerNavigation, Modal, ShowWhen } from "@/components/common";
import { CountriesList, ShippingCalculator } from "../addresses";
import { AddIcon, CalculatorIcon } from "@/components/assets/icons";
import { ModalType, Theme } from "@/enums";
import { useModal } from "@/hooks/useModal";

import { useGetAllFromCountriesRequest } from "@/api/useGetAllFromCountriesRequest";
import { useGetIsFullNecessaryDataOfClientRequest } from "@/api/useGetIsFullNecessaryDataOdClientRequest";
import logAnalyticsEvent from "@/utils/logAnalyticsEvent";

type TProps = {
  children: React.ReactNode;
};

const menuList = [
  {
    title: "get_parcel",
    href: "/parcels/get-parcel/expected-parcels",
  },
  {
    title: "send_parcel",
    href: "/parcels/send-parcel/order-created",
  },
];

const ParcelsListWrapper: FC<TProps> = ({ children }) => {
  const [selectedCountryId, setselectedCountryId] = useState<number>(2);

  const pathname = usePathname();
  const t = useTranslations();
  const { openParcelsModal } = useParcels();
  const theme = useTheme();
  const { closeModal, openModal, modalOpen } = useModal();
  const router = useRouter();

  const { data } = useGetAllFromCountriesRequest();
  const countriesData = data?.data?.data || [];

  const handleSelectCountry = (id: number) => {
    setselectedCountryId(id);
  };

  const isSendParcel = pathname.includes("send-parcel");
  const { data: isFullNecessaryData, isLoading } =
    useGetIsFullNecessaryDataOfClientRequest();

  const handleOpenModal = () => {
    if (theme === Theme.GEORGIA) {
      logAnalyticsEvent("start_add_parcel");
    }

    if (isFullNecessaryData?.data?.data?.isFullData) {
      openParcelsModal({ modalType: ModalType.ADD });
    } else if (!isLoading) {
      toast.warning(t("incomplete_profile_warning_text"), {
        position: "top-center",
      });
      router.push("/profile/personal-information");
    }
  };

  useEffect(() => {
    if (!modalOpen && selectedCountryId !== 2) {
      setselectedCountryId(2);
    }
  }, [modalOpen, selectedCountryId]);

  return (
    <>
      <div className="rounded-[30px] bg-white px-10 pb-8 pt-4">
        <div className="relative flex items-start justify-between p-0">
          <ShowWhen condition={theme === Theme.GEORGIA}>
            <InnerNavigation list={menuList} isParcels />
          </ShowWhen>
          <div />
          <div className="absolute bottom-[-12px] left-0 h-[1px] w-full rounded-tl-sm rounded-tr-sm bg-zinc-100" />
          <div className="flex gap-2">
            {!isSendParcel && (
              <Button
                onClick={openModal}
                className={classNames("!w-full !max-w-[13.94rem] text-nowrap", {
                  "!bg-[#e7fafd] !text-[#008090]": theme !== Theme.UK,
                  "!bg-[#FFE5D2] !text-[#eb7623]": theme === Theme.UK,
                })}
              >
                <CalculatorIcon className="min-w-[20px]" />
                <span className="ml-2">{t("shipping_calculator")}</span>
              </Button>
            )}
            <Button onClick={handleOpenModal} className="max-w-[11.75rem]">
              <AddIcon className="h-[22px] w-[22px]" /> {t("add_new_parcel")}
            </Button>
          </div>
        </div>
        <div className="pt-10">{children}</div>
      </div>
      <Modal
        title="shipping_calculator"
        isOpen={modalOpen}
        closeModal={closeModal}
        paddings="!p-0"
        showCloseIcon
      >
        <div className="px-8">
          <CountriesList
            selectedCountryId={selectedCountryId}
            selectCountry={handleSelectCountry}
            countriesData={countriesData}
          />
        </div>
        <ShippingCalculator
          isSendParcel={isSendParcel}
          isModal
          selectedCountryId={selectedCountryId}
        />
      </Modal>
    </>
  );
};

export default ParcelsListWrapper;
